import { useEffect, useState } from 'react'
import { getSecuritySettings, updateSecuritySettings } from '@/api/security'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'

export function SecuritySettingsCard({ embedded = false }) {
  const [requireMfa, setRequireMfa] = useState(true)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError('')
      try {
        const data = await getSecuritySettings()
        if (!cancelled) setRequireMfa(Boolean(data.require_mfa))
      } catch (err) {
        if (!cancelled) {
          setError(
            typeof err.response?.data?.detail === 'string'
              ? err.response.data.detail
              : 'Could not load security settings.',
          )
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  async function handleSubmit(e) {
    e.preventDefault()
    setSaving(true)
    setError('')
    setSaved(false)
    try {
      const updated = await updateSecuritySettings({ require_mfa: requireMfa })
      setRequireMfa(Boolean(updated.require_mfa))
      setSaved(true)
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Could not save security settings.',
      )
    } finally {
      setSaving(false)
    }
  }

  const body = (
    <div className="space-y-4">
      {!embedded ? (
        <div>
          <h2 className="text-lg font-semibold text-foreground">Security</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Site-wide two-factor authentication default. To let someone sign in with
            password only (for testing), use Turn off on that account under Users.
          </p>
        </div>
      ) : null}

      {error ? (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <form onSubmit={handleSubmit} className="max-w-xl space-y-5">
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              className="mt-1"
              checked={requireMfa}
              onChange={(e) => {
                setRequireMfa(e.target.checked)
                setSaved(false)
              }}
            />
            <span>
              <Label className="text-sm font-semibold text-foreground">
                Require 2FA for all accounts
              </Label>
              <span className="mt-1 block text-sm text-muted-foreground">
                When enabled, users must enrol an authenticator after sign-in, unless
                you have turned 2FA off for that user under Users.
              </span>
            </span>
          </label>

          <div className="flex items-center gap-3">
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
            {saved ? <p className="text-sm text-emerald-700">Saved.</p> : null}
          </div>
        </form>
      )}
    </div>
  )

  if (embedded) return body
  return <div className="rounded-xl border border-border bg-card p-4 shadow-sm sm:p-6">{body}</div>
}
